import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../redux/store";
import { InstantQuotationState } from "../types/InstantQuotation";
import { InstantQuotationVessel } from "../types/quotations/InstantQuotationVessel";
import { axiosGet } from "../services/InspectionService";
import { LocationData } from "../components/InstantQuotation/InstantQuotation";
import axios from "axios";
import { QuotationCreateRequest } from "../types/quotations/QuotationCreateRequest";
import { QuotationRequestState } from "../types/quotations/QuotationRequestState";
import { QuotationStatus } from "../constants/QuotationConstants";
import { getHeader } from "../services/user";
import { QuotationDialogType } from "../types/QuotationDialogType";
import { initialDeleteQuotationModalData } from "../types/QuotationModalData";
import { QuotationUpdateModalData } from "../types/QuotationUpdateModelData";
import { DatePickerType } from "../types/DatePickerType";
import { DeleteInstantQuoteRequest } from "../types/DeleteInstantQuoteRequest";
import { sendGAEvent } from "../services/AnalyticsService";
import {
    CLOSE_QUOTATION_MODAL,
    CREATE_QUOTATION,
    OPEN_QUOTATION_MODAL,
    QUOTATIONS_CLICK
} from "../utils/analytics";

const initialState: InstantQuotationState = {
    modalOpen: false,
    inspectionTypeOptions: [],
    locations: [],
    selectedInspectionType: undefined,
    setSelectedInspectionType: (item: string) => {},
    selectingVessel: false,
    selectedInstantQuotationVessel: undefined,
    setSelectedInstantQuotationVessel: (item: InstantQuotationVessel) => {},
    setSelectedLocation: (item: string) => {},
    selectedLocation: undefined,
    setSelectedStartDate: (startDate: Date) => {},
    setSelectedEndDate: (endDate: Date) => {},
    startDate: null,
    endDate: null,
    quotationRequestState: QuotationRequestState.Idle,
    setOrganizationId: (orgId: number) => {},
    orgId: null,
    setQuotationRequestState: (state: QuotationCreateRequest) => {},
    quotationDialogType: QuotationDialogType.New,
    deleteModalOpen: false,
    deleteModalData: initialDeleteQuotationModalData,
    deleteQuotationRequestState: QuotationRequestState.Idle,
    datePickerType: DatePickerType.Single_Date,
    quotationId: "",
    externalQuotationRequestState: QuotationRequestState.Idle,
    setExternalQuotationRequestState: (state: QuotationCreateRequest) => {},
    invalidShip: false,
    rejectionReasons: []
};

const availableToCustomer = (item: any) =>
    item.AvailableOnCustomerPortal && item.Active;

const alphabeticalComparison = (a: any, b: any) => {
    try {
        const sortFieldA = a.Name.toLowerCase().trim();
        const sortFieldB = b.Name.toLowerCase().trim();

        if (sortFieldA < sortFieldB) {
            return -1;
        }
        if (sortFieldA > sortFieldB) {
            return 1;
        }
        return 0;
    } catch (err) {
        return 0;
    }
};

export const instantQuotationSlice = createSlice({
    name: "instantQuotationSlice",
    initialState,
    reducers: {
        openInstantQuotationDeleteModal: (state, action) => {
            state.deleteModalOpen = true;
            state.deleteModalData = action.payload;
        },
        closeInstantQuotationDeleteModal: (state) => {
            state.deleteModalOpen = false;
            state.deleteQuotationRequestState = QuotationRequestState.Idle;
        },
        openEditQuotationModal: (state, action) => {
            state.quotationDialogType = QuotationDialogType.Edit;
            const editData = action.payload as QuotationUpdateModalData;
            if (editData.startDate != undefined) {
                state.startDate = editData.startDate;
            }

            state.endDate = editData.endDate;
            state.datePickerType = DatePickerType.Single_Date;
            state.quotationId = editData.id;

            if (editData.startDate && editData.endDate) {
                state.datePickerType = DatePickerType.Range;
            }

            state.selectedInstantQuotationVessel = {
                LRIMOShipNo: editData.imo,
                ShipName: editData.vesselName,
                FlagCode: "",
                FlagName: "",
                CountryOfBuild: "",
                CountryOfBuildCode: "",
                ShipTypeName: editData.vesselType
            };

            state.selectedInspectionType = {
                Name: editData.inspectionType,
                Id: editData.inspectionTypeId,
                AvailableOnCustomerPortal: true,
                Active: true
            };

            state.selectedLocation = {
                Code: editData.countryCode,
                Country: editData.location,
                Id: editData.locationId,
                Name: "",
                Region: "",
                SearchField: editData.location
            };

            state.modalOpen = true;
        },
        openInstantQuotationModal: (state) => {
            state.quotationDialogType = QuotationDialogType.New;
            state.modalOpen = true;
            sendGAEvent(QUOTATIONS_CLICK, OPEN_QUOTATION_MODAL);
        },
        closeInstantQuotationModal: (state) => {
            state.quotationDialogType = QuotationDialogType.New;
            state.modalOpen = false;
            state.datePickerType = DatePickerType.Single_Date;
            state.selectedInstantQuotationVessel = undefined;
            state.selectedInspectionType = undefined;
            state.selectedLocation = undefined;
            state.startDate = null;
            state.endDate = null;
            state.invalidShip = false;
            sendGAEvent(QUOTATIONS_CLICK, CLOSE_QUOTATION_MODAL);
        },
        setSelectedInspectionType: (state, action) => {
            state.selectedInspectionType = action.payload;
        },
        openSelectVesselModal: (state) => {
            state.selectingVessel = true;
        },
        closeSelectVesselModal: (state) => {
            state.selectingVessel = false;
        },
        setSelectedInstantQuotationVessel: (state, action) => {
            state.selectedInstantQuotationVessel = action.payload;
        },
        setSelectedStartDate: (state, action) => {
            state.startDate = action.payload;
        },
        setSelectedEndDate: (state, action) => {
            state.endDate = action.payload;
        },
        setSelectedLocation: (state, action) => {
            state.selectedLocation = action.payload;
        },
        setOrganizationId: (state, action) => {
            state.orgId = action.payload;
        },
        setQuotationRequestState: (state, action) => {
            state.quotationRequestState = action.payload;
        },
        setExternalQuotationRequestState: (state, action) => {
            state.externalQuotationRequestState = action.payload;
        },
        setInvalidShip: (state, action) => {
            state.invalidShip = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getInspectionTypesThunk.fulfilled, (state, action) => {
                state.inspectionTypeOptions =
                    action.payload.filter(availableToCustomer);
            })
            .addCase(getLocationsThunk.fulfilled, (state, action) => {
                state.locations = action.payload.sort(alphabeticalComparison);
                state.locations.forEach((location: LocationData) => {
                    location.SearchField = `${location.Name}, ${location.Country}, ${location.Region}`;
                });
            })
            .addCase(postInstantQuotationThunk.fulfilled, (state, action) => {
                const response = action.payload as any;
                const data = response["data"];
                const quotationStatus = data["status"] as QuotationStatus;

                state.quotationId = data["id"];

                if (quotationStatus == QuotationStatus.LEAD) {
                    state.quotationRequestState =
                        QuotationRequestState.CompleteLead;
                }

                if (quotationStatus == QuotationStatus.DRAFT) {
                    state.quotationRequestState =
                        QuotationRequestState.CompleteDraft;
                }

                if (quotationStatus == QuotationStatus.OFFER) {
                    state.quotationRequestState =
                        QuotationRequestState.CompleteOffer;
                }
            })
            .addCase(postInstantQuotationThunk.pending, (state, action) => {
                state.quotationRequestState = QuotationRequestState.Processing;
            })
            .addCase(postInstantQuotationThunk.rejected, (state, action) => {
                const response = action.payload as any;

                if (response === "Ship not found") {
                    state.quotationRequestState =
                        QuotationRequestState.InvalidShip;
                } else {
                    state.quotationRequestState = QuotationRequestState.Failed;
                }
            })
            .addCase(putInstantQuotationThunk.fulfilled, (state, action) => {
                const response = action.payload as any;
                const data = response["data"];
                const quotationStatus = data["status"] as QuotationStatus;

                state.quotationId = data["id"];

                if (quotationStatus == QuotationStatus.LEAD) {
                    state.quotationRequestState =
                        QuotationRequestState.CompleteLead;
                }

                if (quotationStatus == QuotationStatus.DRAFT) {
                    state.quotationRequestState =
                        QuotationRequestState.CompleteDraft;
                }

                if (quotationStatus == QuotationStatus.OFFER) {
                    state.quotationRequestState =
                        QuotationRequestState.CompleteOffer;
                }
            })
            .addCase(putInstantQuotationThunk.pending, (state, action) => {
                state.quotationRequestState = QuotationRequestState.Processing;
            })
            .addCase(putInstantQuotationThunk.rejected, (state, action) => {
                const response = action.payload as any;

                if (response === "Ship not found") {
                    state.quotationRequestState =
                        QuotationRequestState.InvalidShip;
                } else {
                    state.quotationRequestState = QuotationRequestState.Failed;
                }
            })
            .addCase(deleteInstantQuotationThunk.pending, (state, action) => {
                state.deleteQuotationRequestState =
                    QuotationRequestState.Processing;
            })
            .addCase(deleteInstantQuotationThunk.fulfilled, (state, action) => {
                state.deleteQuotationRequestState =
                    QuotationRequestState.CompleteDelete;
            })
            .addCase(deleteInstantQuotationThunk.rejected, (state, action) => {
                state.deleteQuotationRequestState =
                    QuotationRequestState.Failed;
            })
            .addCase(
                postExternalInstantQuotationThunk.fulfilled,
                (state, action) => {
                    const response = action.payload as any;
                    const data = response["data"];
                    const quotationStatus = data["status"] as QuotationStatus;

                    state.quotationId = data["id"];

                    if (quotationStatus == QuotationStatus.LEAD) {
                        state.externalQuotationRequestState =
                            QuotationRequestState.CompleteLead;
                    }

                    if (quotationStatus == QuotationStatus.DRAFT) {
                        state.externalQuotationRequestState =
                            QuotationRequestState.CompleteDraft;
                    }

                    if (quotationStatus == QuotationStatus.OFFER) {
                        state.externalQuotationRequestState =
                            QuotationRequestState.CompleteOffer;
                    }
                }
            )
            .addCase(
                postExternalInstantQuotationThunk.pending,
                (state, action) => {
                    state.externalQuotationRequestState =
                        QuotationRequestState.Processing;
                }
            )
            .addCase(
                postExternalInstantQuotationThunk.rejected,
                (state, action) => {
                    state.externalQuotationRequestState =
                        QuotationRequestState.Failed;
                }
            )
            .addCase(getRejectionReasonsThunk.fulfilled, (state, action) => {
                state.rejectionReasons = action.payload;
            });
    }
});

export const getInspectionTypesThunk = createAsyncThunk(
    "instantQuotation/getInspectionTypes",
    async () => {
        const url = process.env.REACT_APP_INSPECTION_TYPES_URL;
        return axiosGet(url!);
    }
);

export const postInstantQuotationThunk = createAsyncThunk(
    "instantQuotation/post",
    async (data: QuotationCreateRequest, { rejectWithValue }) => {
        const header = await getHeader();
        const url = process.env.REACT_APP_QUOTATION_API_ENDPOINT + "/quotation";
        sendGAEvent(QUOTATIONS_CLICK, CREATE_QUOTATION);

        try {
            const response = await axios.post(url, data, {
                headers: header
            });

            return response;
        } catch (err: any) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const putInstantQuotationThunk = createAsyncThunk(
    "instantQuotation/put",
    async (data: QuotationCreateRequest, { rejectWithValue }) => {
        const header = await getHeader();
        const id = data["id"];
        const url =
            process.env.REACT_APP_QUOTATION_API_ENDPOINT + "/quotation/" + id;

        try {
            const response = await axios.put(url, data, {
                headers: header
            });

            return response;
        } catch (err: any) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const deleteInstantQuotationThunk = createAsyncThunk(
    "instantQuotation/delete",
    async (request: DeleteInstantQuoteRequest) => {
        const header = await getHeader();
        const url =
            process.env.REACT_APP_QUOTATION_API_ENDPOINT +
            "/Quotation/" +
            request.id;

        return axios.delete(url, {
            headers: header,
            data: request.reasonId ? { reasonId: request.reasonId } : undefined
        });
    }
);

export const getLocationsThunk = createAsyncThunk(
    "instantQuotation/getLocations",
    async () => {
        const url = process.env.REACT_APP_LOCATIONS_URL;
        return axiosGet(url!);
    }
);

export const getRejectionReasonsThunk = createAsyncThunk(
    "instantQuotation/getRejectionReasons",
    async () => {
        const header = await getHeader();
        const url =
            process.env.REACT_APP_QUOTATION_API_ENDPOINT +
            "/Quotation/rejection-reasons";

        const response = await axios.get(url, {
            headers: header
        });
        return response.data as Array<{ id: number; customerText: string }>;
    }
);

export const postExternalInstantQuotationThunk = createAsyncThunk(
    "instantQuotation/externalPost",
    async (data: QuotationCreateRequest) => {
        const header = await getHeader();
        const url = process.env.REACT_APP_QUOTATION_API_ENDPOINT + "/quotation";
        return axios.post(url, data, {
            headers: header
        });
    }
);

export const instantQuotationSelector = (state: RootState) =>
    state.InstantQuotation;
export const {
    openInstantQuotationModal,
    closeInstantQuotationModal,
    setSelectedInspectionType,
    openSelectVesselModal,
    closeSelectVesselModal,
    setSelectedInstantQuotationVessel,
    setSelectedEndDate,
    setSelectedStartDate,
    setSelectedLocation,
    setOrganizationId,
    setQuotationRequestState,
    openInstantQuotationDeleteModal,
    closeInstantQuotationDeleteModal,
    openEditQuotationModal,
    setExternalQuotationRequestState,
    setInvalidShip
} = instantQuotationSlice.actions;

export default instantQuotationSlice.reducer;
